/**
 * Quando a aba de uma conversa pisca no acesso rápido.
 *
 * ## O que estava errado
 *
 * O piscar era governado por um único sinalizador, `__isNew`, posto pelo
 * `NewAssignedChatWatcher` quando um chat era distribuído automaticamente. Ele
 * errava dos dois lados -- medido na tela em 14/10/2026, com chat de verdade
 * entrando pelo clientweb:
 *
 * 1. **Piscava para sempre.** `viewedRooms` é persistido no `localStorage`
 *    (`quickAccessViewedRooms`) com o `__isNew` dentro, e ele só era apagado
 *    ao clicar NAQUELA aba. Uma conversa que o operador nunca abriu por ali
 *    seguia piscando entre recargas, entre sessões, por dias.
 *
 * 2. **Não piscava com mensagem nova.** Abri a aba (o sinalizador saiu), fui
 *    para outra tela, o cliente escreveu -- e a aba ficou parada. Não havia
 *    código ligando mensagem a piscar.
 *
 * ## A regra agora
 *
 * Uma só, que é o pedido em uma frase: **pisca enquanto houver novidade e a
 * aba não estiver ativa**. Novidade é mensagem do cliente ainda não lida; e,
 * para o chat recém-atribuído que ainda não recebeu mensagem, o `__isNew`
 * continua valendo até ele ser aberto.
 *
 * ## De onde vem "tem mensagem nova"
 *
 * De `newMessageChats`, o conjunto que o `WebSocketContext` já mantém: ele
 * entra no `NEW_CHAT_MESSAGE_EVENT` (só para `type === 0`, mensagem do
 * cliente) e sai no `MESSAGE_READ_EVENT`. É sinal VIVO e de sessão.
 *
 * Isso importa por dois motivos. Primeiro, ler a conversa apaga o sinal, então
 * o piscar PARA por consequência, sem depender de ninguém clicar no lugar
 * certo -- o "para sempre" vinha de o sinalizador não ter quem o apagasse.
 * Segundo, as mensagens guardadas na aba do `localStorage` são as de quando
 * ela foi criada, congeladas: contá-las ali diria sempre a mesma coisa.
 */

/** O conjunto, aceitando `Set` ou lista, com os ids normalizados para texto. */
const comoConjunto = (salas) => {
    if (salas instanceof Set) return new Set([...salas].map(String));

    return new Set((Array.isArray(salas) ? salas : []).map(String));
};

/**
 * Esta sala tem mensagem nova do cliente?
 *
 * Os ids são comparados como TEXTO: um lado vem do payload da api e o outro do
 * `localStorage`, e número virar texto numa serialização não pode desligar o
 * aviso em silêncio.
 */
export const temMensagemNova = (salasComMensagemNova, roomId) => {
    if (roomId === null || roomId === undefined) return false;

    return comoConjunto(salasComMensagemNova).has(String(roomId));
};

/**
 * A aba desta conversa deve piscar?
 *
 * @param {object} entrada
 * @param {object} entrada.aba a sala como está no acesso rápido
 * @param {Set|Array} entrada.salasComMensagemNova ids com mensagem não lida
 * @param {boolean} entrada.ativa a aba é a conversa aberta agora
 */
export const abaDeveriaPiscar = ({ aba, salasComMensagemNova, ativa }) => {
    // Olhando para ela, não há o que avisar.
    if (ativa) return false;
    if (!aba || aba.room_id === null || aba.room_id === undefined) return false;

    if (temMensagemNova(salasComMensagemNova, aba.room_id)) return true;

    /*
     * O chat recém-atribuído que ainda NÃO recebeu mensagem do cliente.
     *
     * Acontece: a sala nasce com a mensagem automática (tipo 4) e o cliente
     * pode demorar a escrever. Sem este ramo, o chat distribuído para o
     * operador entraria no acesso rápido mudo -- e o pedido é justamente que
     * "um chat novo que não abriu" avise.
     *
     * O `__isNew` deixou de ser persistido (ver `room.reducer`), então ele é um
     * fato da SESSÃO: some ao abrir a conversa, e não volta amanhã.
     */
    return aba.__isNew === true;
};
